import { faArchive } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Button, Popconfirm, Tooltip, notification } from "antd"
import { useArchiveJobCheckQuery, useArchiveJobMutation, useUnarchiveJobMutation } from "../../domain/jobs/jobsQueries"
import { archiveJobRedirectDelayMs, buildArchiveJobDestination, buildArchiveJobNotificationMessage, buildArchiveJobTooltipText } from "./archiveJobModel"

type Props = {
  jobId: number
  jobName?: string
  jobType?: number
  sold?: boolean
}

const ArchiveJob = ({ jobId, jobName, jobType, sold = true }: Props) => {
  const archiveCheckQuery = useArchiveJobCheckQuery(jobId)
  const archiveJobMutation = useArchiveJobMutation()
  const unarchiveJobMutation = useUnarchiveJobMutation()

  const isArchived = archiveCheckQuery.data?.isArchived ?? false
  const isLoading = archiveCheckQuery.isLoading || archiveJobMutation.isLoading || unarchiveJobMutation.isLoading
  const canArchive = archiveCheckQuery.data?.canArchive ?? false
  const isDisabled = isLoading || (!isArchived && !canArchive)

  const handleArchive = () => {
    archiveJobMutation.mutate(jobId, {
      onSuccess: () => {
        notification.success({ message: buildArchiveJobNotificationMessage(jobName, 'archived') })
        window.setTimeout(() => {
          window.location.href = buildArchiveJobDestination(jobType, sold)
        }, archiveJobRedirectDelayMs)
      }
    })
  }

  const handleUnarchive = () => {
    unarchiveJobMutation.mutate(jobId, {
      onSuccess: () => {
        notification.success({ message: buildArchiveJobNotificationMessage(jobName, 'unarchived') })
      }
    })
  }

  const button = <Button
    size="large"
    icon={<FontAwesomeIcon icon={faArchive} />}
    disabled={isDisabled}
    loading={isLoading}
    style={isDisabled ? { pointerEvents: 'none' } : undefined}
  >{isArchived ? 'Unarchive job' : 'Archive job'}</Button>

  if (isDisabled) {
    return <Tooltip title={buildArchiveJobTooltipText({
      isCheckingArchive: archiveCheckQuery.isLoading,
      isArchiving: archiveJobMutation.isLoading,
      isUnarchiving: unarchiveJobMutation.isLoading,
      message: archiveCheckQuery.data?.message
    })}><span style={{ display: 'inline-block' }}>{button}</span></Tooltip>
  }

  return <Popconfirm
    title={isArchived ? 'Unarchive this job?' : 'Archive this job?'}
    description={isArchived
      ? 'This job will return to the default list view.'
      : 'This job will be hidden from default list views but remain available in archived search.'}
    okText={isArchived ? 'Unarchive' : 'Archive'}
    onConfirm={isArchived ? handleUnarchive : handleArchive}
  >{button}</Popconfirm>
}

export default ArchiveJob
